import { useMemo, useState } from "react";
import { liveBulletins, hotTopics, heroTags } from "@/mocks/qxian";

const allSearchable = [
  ...liveBulletins.map((b) => ({ id: b.id, label: b.title, target: "#live", kind: b.category })),
  ...hotTopics.map((t) => ({ id: t.id, label: t.title, target: "#rank", kind: t.category })),
];

export default function Hero() {
  const [keyword, setKeyword] = useState("");
  const [focused, setFocused] = useState(false);

  const suggestions = useMemo(() => {
    const k = keyword.trim();
    if (!k) return [];
    return allSearchable.filter((s) => s.label.includes(k)).slice(0, 6);
  }, [keyword]);

  return (
    <section id="top" className="relative w-full min-h-[520px] md:min-h-[600px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20editorial%20backdrop%20of%20warm%20vermilion%20red%20and%20amber%20gold%20light%20streaks%20flowing%20across%20a%20deep%20charcoal%20canvas%2C%20cinematic%20ambient%20glow%2C%20subtle%20film%20grain%20and%20soft%20bokeh%2C%20dramatic%20spotlight%20beams%20converging%20toward%20center%2C%20rich%20layered%20texture%2C%20atmospheric%20high-contrast%20composition%2C%20no%20people%2C%20no%20text%2C%20no%20logos&width=1600&height=900&seq=qxian-hero&orientation=landscape"
          alt="吃瓜前线暖色光效背景图"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-900/60 to-foreground-950/85"></div>
      </div>

      <div className="relative mx-auto w-full max-w-6xl px-4 md:px-6 pt-28 pb-16 md:pt-36 md:pb-20 flex flex-col items-center text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 border border-background-50/25 px-4 py-1.5 text-xs text-background-50 backdrop-blur">
          <i className="ri-flashlight-fill text-accent-400"></i>
          全网热瓜 · 一手同步 · 按时间线整理
        </span>

        <h1 className="mt-6 font-heading text-3xl md:text-5xl font-black leading-tight text-background-50">
          吃瓜前线，持续跟进
          <span className="block mt-2 text-accent-400">不用来回翻门的进展同步站</span>
        </h1>
        <p className="mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-background-200/90">
          最新进展、动态快报逐条更新，事件再长也能快速对齐后续。
          首页按时间线同步整理，随时刷新都有新变化，别再错过瓜的关键后续。
        </p>

        <div className="relative mt-8 w-full max-w-xl">
          <div className="flex items-center gap-2 rounded-full bg-background-50/95 p-1.5 shadow-lg">
            <i className="ri-search-line text-foreground-400 pl-3 text-lg"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 150)}
              placeholder="搜艺人、节目、事件关键词…"
              className="flex-1 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              aria-label="搜索热瓜关键词"
            />
            {keyword && (
              <button
                onClick={() => setKeyword("")}
                className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700"
                aria-label="清空"
              >
                <i className="ri-close-circle-fill text-lg"></i>
              </button>
            )}
            <a
              href="#rank"
              onClick={() => setKeyword("")}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 text-background-50 px-5 py-2.5 text-sm hover:bg-primary-600 transition-colors"
            >
              搜索
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
          {focused && suggestions.length > 0 && (
            <div className="absolute inset-x-0 top-full mt-2 rounded-xl bg-background-50 shadow-lg border border-background-200 p-2 text-left fade-up z-20">
              {suggestions.map((s) => (
                <a
                  key={s.id}
                  href={s.target}
                  className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 hover:bg-background-100"
                >
                  <span className="text-sm text-foreground-800 truncate">{s.label}</span>
                  <span className="shrink-0 text-xs text-foreground-400">{s.kind}</span>
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {heroTags.map((t) => (
            <a
              key={t}
              href="#rank"
              className="whitespace-nowrap rounded-full bg-background-50/10 border border-background-50/20 px-3 py-1 text-xs text-background-100 hover:bg-background-50/20 transition-colors"
            >
              # {t}
            </a>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-3 gap-4 md:gap-8 w-full max-w-2xl">
          {[
            { num: "128", label: "今日快报" },
            { num: "47", label: "正在跟进事件" },
            { num: "6 分钟", label: "平均更新间隔" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl bg-background-50/10 border border-background-50/15 px-3 py-4 backdrop-blur">
              <div className="font-heading text-xl md:text-2xl font-bold text-background-50">{s.num}</div>
              <div className="mt-1 text-xs text-background-200/80">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="relative border-t border-background-50/15 bg-foreground-950/60 backdrop-blur">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-3 flex items-center gap-3 overflow-hidden">
          <span className="shrink-0 inline-flex items-center gap-1.5 text-xs text-background-50">
            <span className="live-dot w-2 h-2 rounded-full bg-accent-400 inline-block"></span>
            直播流
          </span>
          <div className="flex items-center gap-8 overflow-hidden whitespace-nowrap text-xs text-background-200/85">
            {liveBulletins.slice(0, 4).map((b) => (
              <span key={b.id} className="truncate">
                [{b.time}] {b.title}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}